/**
 * moneyFormat - thousands grouping for money inputs.
 *
 * `600000` in a bare input is a number you have to count digits on. `600,000`
 * is an amount you read. The grouping is DISPLAY ONLY: everything that leaves
 * the input - state, validation, the save payload - stays a plain numeric
 * string, because a comma in `Number("600,000")` is `NaN`.
 *
 * Separators come from the locale rather than being hardcoded to `,` and `.`,
 * so a German user gets `600.000,50` and an Indian user gets the lakh grouping
 * `6,00,000`. The number of decimal places comes from the CURRENCY: two for
 * EUR and USD, none for JPY, three for KWD.
 *
 * These are pure functions. The caret bookkeeping lives here too, since
 * "reformat as you type" is only usable if the cursor stays where the user
 * left it - the two belong together and are tested together.
 */

export interface MoneyFormatSpec {
  /** Thousands separator for the locale - "," in en-GB, "." in de-DE. */
  group: string;
  /** Decimal separator - "." in en-GB, "," in de-DE. */
  decimal: string;
  /** How many decimals the currency has. 0 for JPY, 2 for EUR, 3 for KWD. */
  maxFractionDigits: number;
  locale: string;
}

const specCache = new Map<string, MoneyFormatSpec>();

/** The separators and precision for a locale/currency pair. */
export function moneyFormatSpec(locale = 'en', currency?: string): MoneyFormatSpec {
  const key = `${locale}|${(currency || '').toLowerCase()}`;
  const cached = specCache.get(key);
  if (cached) return cached;

  let group = ',';
  let decimal = '.';
  let maxFractionDigits = 2;

  try {
    // 1234.5 is enough to expose both separators in every locale.
    for (const part of new Intl.NumberFormat(locale).formatToParts(1234.5)) {
      if (part.type === 'group') group = part.value;
      if (part.type === 'decimal') decimal = part.value;
    }
  } catch {
    /* keep the en defaults */
  }

  if (currency) {
    try {
      maxFractionDigits = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency.toUpperCase(),
      }).resolvedOptions().maximumFractionDigits ?? 2;
    } catch {
      // An unknown or malformed code is not a reason to refuse decimals.
      maxFractionDigits = 2;
    }
  }

  const spec = { group, decimal, maxFractionDigits, locale };
  specCache.set(key, spec);
  return spec;
}

/**
 * Display string → plain numeric string.
 *
 * The inverse of `groupMoney`, and what the caller's `onChange` receives.
 * Everything that is not a digit or the decimal point goes, so the result
 * always survives `Number()`.
 */
export function parseMoney(display: string, spec: MoneyFormatSpec): string {
  if (!display) return '';

  /*
    The GROUP separator goes first, unconditionally - "1,234" in en-GB is one
    thousand two hundred and thirty-four, not 1.234. Whatever separator is
    left over is then read as the decimal point, so a German user's "," works
    and so does the "." from a numpad in a locale that groups with ",". The
    two roles cannot both be served by the same character, and grouping wins.
  */
  const normalised = display.replace(
    new RegExp(`[${escapeForClass(spec.group)}\\s]`, 'g'),
    ''
  );

  let seenDot = false;
  let out = '';
  for (const char of normalised) {
    if (char >= '0' && char <= '9') {
      out += char;
    } else if (char === spec.decimal || char === '.' || char === ',') {
      /*
        A currency with no decimals STOPS here rather than skipping the
        point - skipping it would splice the fraction onto the integer and
        turn ¥1000.99 into ¥100099.
      */
      if (spec.maxFractionDigits === 0) break;
      if (seenDot) continue;
      seenDot = true;
      out += '.';
    }
  }

  if (out === '.') return '';
  return out;
}

/**
 * Plain numeric string → grouped display string.
 *
 * Written for a value being TYPED, not a finished one: a trailing separator
 * survives so "600000." does not delete itself the moment it is entered, and
 * nothing is padded to two decimals mid-edit.
 */
export function groupMoney(plain: string, spec: MoneyFormatSpec): string {
  if (plain === '' || plain == null) return '';

  const [rawInt = '', rawFraction] = String(plain).split('.');
  const intDigits = rawInt.replace(/\D/g, '');

  let grouped: string;
  if (intDigits === '') {
    grouped = '';
  } else {
    try {
      // BigInt, so a very long entry groups correctly instead of going
      // through a float and losing its tail.
      grouped = new Intl.NumberFormat(spec.locale).format(BigInt(intDigits));
    } catch {
      grouped = fallbackGroup(intDigits, spec.group);
    }
  }

  if (rawFraction === undefined) return grouped;
  if (spec.maxFractionDigits === 0) return grouped;

  const fraction = rawFraction.replace(/\D/g, '').slice(0, spec.maxFractionDigits);
  // `grouped || '0'` so a value typed as ".5" reads as "0.5" rather than ".5".
  return `${grouped || '0'}${spec.decimal}${fraction}`;
}

/** Groups in threes - only reached if `Intl` refuses the locale. */
function fallbackGroup(digits: string, group: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, group);
}

function escapeForClass(value: string): string {
  return value.replace(/[\\\]^-]/g, '\\$&');
}

/* ── Caret ───────────────────────────────────────────────────────────
   Reformatting rewrites the whole string, which sends the caret to the
   end - so typing a digit in the middle of "600,000" jumps you out of it.
   The fix is to count in DIGITS rather than characters: remember how many
   digits sat before the caret, then find that position again in the new
   string, wherever the separators landed. */

/** How many digits precede `caret` in `text`. */
export function digitsBefore(text: string, caret: number): number {
  let count = 0;
  for (let i = 0; i < Math.min(caret, text.length); i += 1) {
    const char = text[i];
    if (char >= '0' && char <= '9') count += 1;
  }
  return count;
}

/** The offset in `text` that sits just after its `count`-th digit. */
export function caretAfterDigits(text: string, count: number): number {
  if (count <= 0) {
    // Before the first digit, not before a leading separator.
    return 0;
  }
  let seen = 0;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (char >= '0' && char <= '9') {
      seen += 1;
      if (seen === count) return i + 1;
    }
  }
  return text.length;
}
